import {
    ApiContentSection,
    ApiShopperLandingPageLink,
    ApiProductGridType,
    ApiSectionType,
    ApiProductGridLayout,
    DEFAULT_PRODUCT_GRID_COUNT,
} from '@storefronts/common-types'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { PLPItemsGrid, ItemsGridEmptyState } from '../full-screen/PLPDrawer'
import {
    SectionContainer,
    SectionTitle,
    ProductGridContainer,
    ProductScrollContainer,
    ProductScrollItem,
} from './Shared'
import { ViddyLPContext } from '../../../context/viddyLPContext'
import { LandingPageLinksContext } from '../../../context/LandingPageLinksContext'
import {
    useProductSort,
    ProductSortProvider,
} from '../../../context/ProductSortContext'
import { useColumnCount } from '../../../context/ProductLayoutContext'
import {
    sortProducts,
    getSectionDefaultSort,
} from '../../../utils/productSorting'
import { ProductControls } from '../../shared/ProductControls'
import {
    ProductFilterPills,
    useFilterState,
} from '../../shared/ProductFilterDialog'
import { useRecentlyViewedProducts } from '../../../hooks/useRecentlyViewedProducts'

type PLPContentSection = Extract<
    ApiContentSection,
    { type: ApiSectionType.PLP }
>

function sortProductsByOriginalOrder(
    products: ApiShopperLandingPageLink[],
    listOfOrderedHashes: string[],
): ApiShopperLandingPageLink[] {
    if (listOfOrderedHashes.length === 0) {
        return products
    }
    // Create a map for O(1) lookup instead of O(n) indexOf
    const hashToIndex = new Map<string, number>()
    listOfOrderedHashes.forEach((hash, index) => {
        hashToIndex.set(hash, index)
    })
    return [...products].sort((a, b) => {
        const indexA = hashToIndex.get(a.linkHash) ?? Infinity
        const indexB = hashToIndex.get(b.linkHash) ?? Infinity
        return indexA - indexB
    })
}

export const PLPSection: React.FC<{
    section: PLPContentSection
    isBelowFold?: boolean
}> = ({ section, isBelowFold = true }) => {
    const defaultSort = getSectionDefaultSort(section)
    return (
        <ProductSortProvider key={defaultSort} defaultSort={defaultSort}>
            <PLPSectionContent section={section} isBelowFold={isBelowFold} />
        </ProductSortProvider>
    )
}

const PLPSectionContent: React.FC<{
    section: PLPContentSection
    isBelowFold: boolean
}> = ({ section, isBelowFold }) => {
    const { landingPage } = useContext(ViddyLPContext)
    const { resolvedLandingPageLinks, fetchAndCacheLinksForHashes } =
        useContext(LandingPageLinksContext)
    const { sortOption } = useProductSort()
    const columnCount = useColumnCount()

    const isDynamicProductGrid =
        section.productGridType === ApiProductGridType.BEST_SELLERS ||
        section.productGridType === ApiProductGridType.OFTEN_BOUGHT_TOGETHER ||
        section.productGridType === ApiProductGridType.RECENTLY_VIEWED ||
        section.productGridType === ApiProductGridType.SHOP_THE_COLLECTION

    // Only Best Sellers and OBT should fall back to generic landingPageLinks when empty
    // Shop the Collection should NOT fall back (would show unrelated products)
    const shouldFallbackToLandingPageLinks =
        section.productGridType === ApiProductGridType.BEST_SELLERS ||
        section.productGridType === ApiProductGridType.OFTEN_BOUGHT_TOGETHER

    const isRecentlyViewed =
        section.productGridType === ApiProductGridType.RECENTLY_VIEWED

    // Get recently viewed products from session storage (only used for RECENTLY_VIEWED grids)
    const recentlyViewedProducts = useRecentlyViewedProducts(
        section.productCount || DEFAULT_PRODUCT_GRID_COUNT,
    )

    // Known links: filter resolved links by visibleProducts hashes
    // For dynamic grids (Best Sellers, OBT) without visibleProducts, fall back to landingPageLinks
    // For Recently Viewed, use the recentlyViewedProducts from session storage
    // For non-dynamic grids, empty visibleProducts means no products selected
    const visibleKnownLinks = useMemo(() => {
        // Recently Viewed: use products from session storage
        if (isRecentlyViewed) {
            return recentlyViewedProducts
        }
        // Best Sellers/OBT: fall back to landingPageLinks when visibleProducts is empty
        // Shop the Collection: return empty (section will be hidden)
        if (
            shouldFallbackToLandingPageLinks &&
            !section.visibleProducts?.length
        ) {
            const allLinks = landingPage?.landingPageLinks ?? []
            const count = section.productCount || DEFAULT_PRODUCT_GRID_COUNT
            return allLinks.slice(0, count)
        }
        // Non-dynamic grids with undefined visibleProducts: show all products
        if (!section.visibleProducts) {
            return landingPage?.landingPageLinks ?? []
        }
        const hashSet = new Set(section.visibleProducts)
        return resolvedLandingPageLinks.filter((link) =>
            hashSet.has(link.linkHash),
        )
    }, [
        section.visibleProducts,
        section.productCount,
        landingPage?.landingPageLinks,
        resolvedLandingPageLinks,
        shouldFallbackToLandingPageLinks,
        isRecentlyViewed,
        recentlyViewedProducts,
    ])

    // Target hashes in display order. Dynamic grids always cap (with a
    // default); static lists cap when productCount is set.
    const targetHashes = useMemo(() => {
        const hashes = section.visibleProducts ?? []
        const count = isDynamicProductGrid
            ? section.productCount || DEFAULT_PRODUCT_GRID_COUNT
            : section.productCount
        return count ? hashes.slice(0, count) : hashes
    }, [section.visibleProducts, isDynamicProductGrid, section.productCount])

    // Known links sorted by target order, capped the same way as targetHashes.
    const cappedKnownLinks = useMemo(() => {
        const sorted =
            targetHashes.length > 0
                ? sortProductsByOriginalOrder(visibleKnownLinks, targetHashes)
                : visibleKnownLinks
        const count = isDynamicProductGrid
            ? section.productCount || DEFAULT_PRODUCT_GRID_COUNT
            : section.productCount
        return count ? sorted.slice(0, count) : sorted
    }, [
        visibleKnownLinks,
        targetHashes,
        isDynamicProductGrid,
        section.productCount,
    ])

    const [products, setProducts] =
        useState<ApiShopperLandingPageLink[]>(cappedKnownLinks)

    // Fetch any missing links via context, otherwise use cappedKnownLinks
    useEffect(() => {
        let isCancelled = false

        const missingHashes = targetHashes.filter(
            (hash) => !cappedKnownLinks.some((link) => link.linkHash === hash),
        )

        if (!missingHashes.length) {
            setProducts(cappedKnownLinks)
            return
        }

        fetchAndCacheLinksForHashes(targetHashes)
            .then((all) => {
                if (!isCancelled) {
                    setProducts(
                        targetHashes.length > 0
                            ? sortProductsByOriginalOrder(all, targetHashes)
                            : all,
                    )
                }
            })
            .catch((error) => {
                if (!isCancelled) {
                    console.error('Error fetching landing page links:', error)
                    setProducts(cappedKnownLinks)
                }
            })

        return () => {
            isCancelled = true
        }
    }, [targetHashes, cappedKnownLinks, fetchAndCacheLinksForHashes])

    const filterState = useFilterState()

    const filteredProducts = useMemo(() => {
        return filterState.getFilteredProducts(products, landingPage?.discount)
    }, [products, filterState.getFilteredProducts, landingPage?.discount])

    // Determine which products to sort and sort them
    const sortedProducts = useMemo(() => {
        // Sort and return as array of link hashes
        return sortProducts(
            filteredProducts,
            sortOption,
            landingPage?.discount || null,
        ).map((product) => product.linkHash)
    }, [filteredProducts, sortOption, landingPage?.discount])

    // For dynamic grids, hide the entire section (including title) if no products
    if (isDynamicProductGrid && products.length === 0) {
        return null
    }

    return (
        <SectionContainer>
            {section.sectionTitle && (
                <SectionTitle
                    isHorizontalScroll={
                        section.layoutStyle ===
                        ApiProductGridLayout.HORIZONTAL_SCROLL
                    }
                >
                    {section.sectionTitle}
                </SectionTitle>
            )}
            {section.layoutStyle !== ApiProductGridLayout.HORIZONTAL_SCROLL && (
                <>
                    <ProductControls
                        products={products}
                        filterState={filterState}
                    />
                    <ProductFilterPills
                        activeFilters={filterState.selectedFilters}
                        removeFilter={filterState.removeFilter}
                        priceRange={filterState.priceRange}
                        removePriceFilter={filterState.removePriceFilter}
                        currencySymbol={landingPage?.client?.currencySymbol}
                    />
                </>
            )}
            {section.layoutStyle === ApiProductGridLayout.HORIZONTAL_SCROLL ? (
                products.length > 0 && filteredProducts.length === 0 ? (
                    <ItemsGridEmptyState>
                        No products match your filters. Try adjusting your
                        selections.
                    </ItemsGridEmptyState>
                ) : (
                    <ProductScrollContainer>
                        {sortedProducts.map((hash, cardIndex) => (
                            <ProductScrollItem key={hash}>
                                <PLPItemsGrid
                                    visibleProducts={[hash]}
                                    products={filteredProducts}
                                    isHorizontalScroll={true}
                                    isBelowFold={isBelowFold || cardIndex > 0}
                                />
                            </ProductScrollItem>
                        ))}
                    </ProductScrollContainer>
                )
            ) : (
                <ProductGridContainer columnCount={columnCount}>
                    {products.length > 0 && filteredProducts.length === 0 && (
                        <ItemsGridEmptyState>
                            No products match your filters. Try adjusting your
                            selections.
                        </ItemsGridEmptyState>
                    )}
                    <PLPItemsGrid
                        visibleProducts={sortedProducts}
                        products={filteredProducts}
                        isBelowFold={isBelowFold}
                    />
                </ProductGridContainer>
            )}
        </SectionContainer>
    )
}
